import * as PIXI from 'pixi.js';
import { useDrawingStore } from '../stores/drawingStore';

/**
 * DrawingLayer
 *
 * Renders freehand drawing strokes on the map canvas.
 * Subscribes to drawingStore and redraws whenever strokes change.
 */
export class DrawingLayer {
  public container: PIXI.Container;
  private graphics: PIXI.Graphics;
  private unsubscribe: (() => void) | null = null;

  constructor() {
    this.container = new PIXI.Container();
    this.container.zIndex = 3; // above grid (1-2), below tokens (~4-5)

    this.graphics = new PIXI.Graphics();
    this.container.addChild(this.graphics);

    // Subscribe to store changes
    this.unsubscribe = useDrawingStore.subscribe(() => {
      this.redraw();
    });

    // Initial draw
    this.redraw();
  }

  private redraw(): void {
    const { strokes, currentStroke } = useDrawingStore.getState();
    const g = this.graphics;
    g.clear();

    // Draw completed strokes
    for (const stroke of strokes) {
      this.drawStroke(g, stroke.points, stroke.color, stroke.width, 0.85);
    }

    // Draw in-progress stroke
    if (currentStroke && currentStroke.points.length >= 2) {
      this.drawStroke(g, currentStroke.points, currentStroke.color, currentStroke.width, 0.5);
    }
  }

  private drawStroke(
    g: PIXI.Graphics,
    points: Array<{ x: number; y: number }>,
    color: number,
    width: number,
    alpha: number,
  ): void {
    if (points.length < 2) return;

    g.lineStyle(width, color, alpha);
    g.moveTo(points[0].x, points[0].y);

    // Use quadratic curves for smoother lines when we have enough points
    if (points.length === 2) {
      g.lineTo(points[1].x, points[1].y);
    } else {
      for (let i = 1; i < points.length - 1; i++) {
        const xc = (points[i].x + points[i + 1].x) / 2;
        const yc = (points[i].y + points[i + 1].y) / 2;
        g.quadraticCurveTo(points[i].x, points[i].y, xc, yc);
      }
      // Final segment
      const last = points[points.length - 1];
      g.lineTo(last.x, last.y);
    }
  }

  destroy(): void {
    if (this.unsubscribe) {
      this.unsubscribe();
      this.unsubscribe = null;
    }
    this.graphics.destroy();
    this.container.destroy({ children: true });
  }
}
